import React, { createContext, useCallback, useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode"; // Import jwt-decode to decode token
import axios from "axios";
import { logout as requestLogout } from "./authService";
import {
  clearStoredAuthToken,
  getStoredAuthToken,
  storeAuthToken,
} from "./authStorage";
import { notifyPageVisit } from "./visitNotifications";

// Create the AuthContext
const AuthContext = createContext();

// Create a custom hook to use the AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component to wrap the app
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Store the user info
  const [token, setToken] = useState(null); // Store the token
  const [loading, setLoading] = useState(true); // Loading state to track token check

  const mergeUserProfile = useCallback((profile) => {
    if (!profile || typeof profile !== "object") return null;
    setUser((previousUser) => ({
      ...(previousUser || {}),
      ...profile,
      id: profile.id || profile._id || previousUser?.id,
      userId: profile.userId || profile._id || profile.id || previousUser?.userId,
    }));
    return profile;
  }, []);

  const refreshUserProfile = useCallback(async () => {
    const response = await axios.get("/auth/profile");
    return mergeUserProfile(response?.data || null);
  }, [mergeUserProfile]);

  const handleSessionInvalidation = () => {
    setToken(null);
    setUser(null);
    delete axios.defaults.headers["Authorization"];
    clearStoredAuthToken();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
  };

  // Check if token exists in localStorage and set user state on app load
  useEffect(() => {
    const storedToken = getStoredAuthToken();
    if (storedToken) {
      try {
        const decodedToken = jwtDecode(storedToken);
        if (decodedToken.exp * 1000 > Date.now()) {
          setToken(storedToken);
          setUser(decodedToken); // Set user info from the token
          axios.defaults.headers["Authorization"] = storedToken;
          refreshUserProfile().catch(() => {});
          notifyPageVisit({ eventType: "authenticated_page_load" });
        } else {
          clearStoredAuthToken(); // Remove expired token
        }
      } catch (error) {
        console.error("Invalid token:", error);
        clearStoredAuthToken();
      }
    }
    setLoading(false); // Finished checking for token
  }, [refreshUserProfile]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleProfileChange = (event) => {
      mergeUserProfile(event?.detail || null);
    };
    window.addEventListener("xima-profile-change", handleProfileChange);
    return () => window.removeEventListener("xima-profile-change", handleProfileChange);
  }, [mergeUserProfile]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = Number(error?.response?.status || 0);
        const code = String(error?.response?.data?.code || "").trim();
        if (status === 401 && code === "SESSION_INVALIDATED") {
          handleSessionInvalidation();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  // Function to log in the user
  const login = (userToken, options = {}) => {
    const shouldPersist =
      typeof options?.persist === "boolean" ? options.persist : true;
    setToken(userToken);
    storeAuthToken(userToken, {
      persist: shouldPersist,
    });
    axios.defaults.headers["Authorization"] = userToken;
    const decodedToken = jwtDecode(userToken);
    setUser(decodedToken); // Decode and set user info
    refreshUserProfile().catch(() => {});
    notifyPageVisit({ eventType: "login" });
  };

  // Function to log out the user
  const logout = async () => {
    try {
      if (token) {
        await requestLogout();
      }
    } catch (error) {
      // Even if server logout fails, always clear local auth state.
    } finally {
      handleSessionInvalidation();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, refreshUserProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
